if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const bundles = this.getBundles();

        if (bundles.length > 0) {
          // Product bundle: hoofdproduct + aangevinkte bundelproducten als één items-array (JSON)
          config.headers['Content-Type'] = 'application/json';

          const body = { items: this.buildBundleItems(formData, bundles) };
          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = JSON.stringify(body);
        } else {
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Bundle-response (items-array) normaliseren zodat cart-notification/cart-drawer
            // de key en het id van het hoofdproduct (laatste item in de request) kunnen gebruiken
            if (bundles.length > 0 && Array.isArray(response.items) && response.items.length > 0) {
              const mainItem = response.items[response.items.length - 1];
              response.key = response.key || mainItem.key;
              response.id = response.id || mainItem.id;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              if (window.WoodypanelMaakCompleet && window.WoodypanelMaakCompleet.maybeRedirectAfterAtc()) {
                return; // Maak Compleet started a redirect to /pages/maak-compleet
              }
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (!this.error && bundles.length > 0) this.resetBundles(bundles);
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      /* ── Product bundle ── */

      getBundles() {
        if (!this.form.id) return [];
        return Array.from(
          document.querySelectorAll(`details[form="${this.form.id}"] input[name="bundles"]:checked`)
        );
      }

      getBundleQuantity(input) {
        if (!input || input.disabled || !input.checked) return 1;
        const qtyElement = input
          .closest('label.product-bundle__info')
          ?.querySelector('.js-bundle-qty, [data-bundle-qty]');
        if (!qtyElement) return 1;
        const parsedQty = parseInt(qtyElement.value ?? qtyElement.getAttribute('data-bundle-qty'), 10);
        return Number.isNaN(parsedQty) || parsedQty < 1 ? 1 : parsedQty;
      }

      buildBundleItems(formData, bundles) {
        const items = bundles
          .map((input) => ({
            id: parseInt(input.value, 10),
            quantity: this.getBundleQuantity(input),
          }))
          .reverse();

        // Hoofdproduct opbouwen uit het formulier (id, quantity, properties, selling plan)
        const mainItem = {
          id: parseInt(formData.get('id'), 10),
          quantity: parseInt(formData.get('quantity'), 10) || 1,
        };

        for (const [name, value] of formData.entries()) {
          if (name.includes('properties')) {
            const property = /(?:^(properties\[))(.*?)(?:\])/.exec(name)?.[2];
            if (property) {
              mainItem.properties = mainItem.properties || {};
              mainItem.properties[property] = value;
            }
          } else if (name === 'selling_plan' && value) {
            mainItem.selling_plan = value;
          }
        }

        items.push(mainItem);
        return items;
      }

      resetBundles(bundles) {
        bundles.forEach((input) => {
          input.checked = false;
          const qtyElement = input
            .closest('label.product-bundle__info')
            ?.querySelector('.js-bundle-qty, [data-bundle-qty]');
          if (qtyElement) qtyElement.value = '1';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    }
  );
}
